/**
 * Imports the necessary dependencies for a React component that includes a sidebar and uses react-router-dom.
 * @module React
 * @module Sidebar
 * @module Outlet
 */
import React, { useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import Header from "./header";
import Sidebar from "./sidebar";
import SidebarClubManager from "./sidebarClubManager";
import HeaderOrganiser from "./headerOrganiser";
import useIdleLogout from "../../hooks/useIdleLogout";
import { rememberClubStaffPath } from "../../helpers/authSession";

import Constant from "../../util/constant";
import {
  MOBILE_SIDEBAR_BREAKPOINT_PX,
  toggleBodySidebarClasses,
} from "../../util/sidebarToggle";

/**
 * Callback function to toggle the menu layout based on the screen width.
 * @returns None
 */
function Layout(props) {
  useIdleLogout();
  const location = useLocation();
  const role = window.localStorage.getItem("role");
  /**
   * Toggles the menu based on the viewport width.
   * If the screen width is less than or equal to 1024 pixels, it only toggles `sidebar-enable` and clears `vertical-collpsed` when opening so labels stay visible.
   * On viewports wider than 1024px, the menu stays fully expanded (no icon-only rail).
   * @returns None
   */
  const path = window.location.pathname || "";
  /** Club manager event pages reuse organiser table styles; those rules zero margin-left, so we add a shell class to keep the fixed sidebar offset. */
  const isClubStaffEventRoutes =
    (role === Constant.ROLES.CLUB_MANAGER ||
      role === Constant.ROLES.CLUB_EMPLOYEE) &&
    path.startsWith("/events-list");
  const isEventDetailPath = path === "/events-list/event-detail";

  const toggleMenuCallback = () => {
    toggleBodySidebarClasses();
  };

  useEffect(() => {
    const stripDesktopOverlayClasses = () => {
      if (window.innerWidth > MOBILE_SIDEBAR_BREAKPOINT_PX) {
        document.body.classList.remove("vertical-collpsed");
        document.body.classList.remove("sidebar-enable");
      }
    };
    stripDesktopOverlayClasses();
    window.addEventListener("resize", stripDesktopOverlayClasses);
    return () => window.removeEventListener("resize", stripDesktopOverlayClasses);
  }, []);

  useEffect(() => {
    if (
      role === Constant.ROLES.CLUB_MANAGER ||
      role === Constant.ROLES.CLUB_EMPLOYEE
    ) {
      rememberClubStaffPath(location.pathname);
    }
  }, [location.pathname, role]);

  return (
    /**
     * Renders the main layout of the application.
     * @returns {React.Element} - The JSX element representing the main layout.
     */
    <React.Fragment>
      <div id="layout-wrapper">
        {/* {dimensions.width < 1280 && ( */}
        {role === Constant.ROLES.ADMIN ? (
          <>
            <Header toggleMenuCallback={toggleMenuCallback} />

            <Sidebar />
          </>
        ) : role === Constant.ROLES.CLUB_MANAGER ||
          role === Constant.ROLES.CLUB_EMPLOYEE ? (
          <>
            <HeaderOrganiser toggleMenuCallback={toggleMenuCallback} />
            <SidebarClubManager />
          </>
        ) : (
          <HeaderOrganiser />
        )}

        <div
          className={
            role === Constant.ROLES.ADMIN && isEventDetailPath
              ? "mainContent overflow"
              : role === Constant.ROLES.ORGANISER || isClubStaffEventRoutes
              ? `mainContent organiserContainStyle${
                  isClubStaffEventRoutes ? " club-staff-event-shell" : ""
                }${isEventDetailPath ? " overflow" : ""}`
              : role === Constant.ROLES.CLUB_MANAGER ||
                role === Constant.ROLES.CLUB_EMPLOYEE
              ? "mainContent"
              : role === Constant.ROLES.ADMIN
              ? "mainContent"
              : ""
          }
        >
          {/*
            theme.css: .mainContent .content { margin-top: var(--header-height) } clears the
            fixed #page-topbar. Organiser and club-staff /events-list use organiserContainStyle
            (.page-title-box padding-top) without .content to avoid double vertical offset.
          */}
          {role === Constant.ROLES.ORGANISER || isClubStaffEventRoutes ? (
            <Outlet />
          ) : (
            <div className="content">
              <Outlet />
            </div>
          )}
        </div>
      </div>
    </React.Fragment>
  );
}

export default Layout;
